// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.
import type { SemVer } from "./types.ts";
import { parse } from "./parse.ts";

/**
 * Returns the parsed SemVer, or `undefined` if it's not valid.
 *
 * @example Usage
 * ```ts
 * import { tryParse } from "@std/semver/try-parse";
 * import { assertEquals } from "@std/assert";
 *
 * assertEquals(tryParse("1.2.3"), { major: 1, minor: 2, patch: 3, prerelease: [], build: [] });
 * assertEquals(tryParse("1.2.3-alpha"), { major: 1, minor: 2, patch: 3, prerelease: ["alpha"], build: [] });
 * assertEquals(tryParse("1.2.3+build"), { major: 1, minor: 2, patch: 3, prerelease: [], build: ["build"] });
 * assertEquals(tryParse("1.2.3-alpha.1+build.1"), { major: 1, minor: 2, patch: 3, prerelease: ["alpha", 1], build: ["build", "1"] });
 * assertEquals(tryParse(" invalid "), undefined);
 * ```
 *
 * @param value The version string to parse
 * @returns A valid SemVer or `undefined`
 */
export function tryParse(value: string): SemVer | undefined {
  try {
    return parse(value);
  } catch {
    return undefined;
  }
}

// denoCacheMetadata={"headers":{"x-goog-stored-content-encoding":"identity","x-guploader-uploadid":"AHmUCY3FJLI21rtXq-kFZljZAIqM5Xrxm8Dc0ABTGIf1t-FT3nBK8_sq6gpUc8UM2q8btlEIYhs","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","server":"UploadServer","accept-ranges":"bytes","x-robots-tag":"noindex","x-goog-metageneration":"1","content-length":"1131","content-type":"text/typescript","access-control-allow-origin":"*","date":"Fri, 08 Nov 2024 09:36:15 GMT","x-jsr-cache-id":"IAD","x-content-type-options":"nosniff","last-modified":"Wed, 04 Sep 2024 10:41:42 GMT","via":"1.1 google","etag":"\"23e534f1eeb64072adbe714aa9fbf88d\"","expires":"Sat, 08 Nov 2025 09:36:15 GMT","x-jsr-cache-status":"hit","x-goog-hash":"crc32c=tdPpFQ==,md5=I+U08e62QHKtvnFKqfv4jQ==","age":"2026","access-control-expose-headers":"*","x-goog-generation":"1725446502027674","cross-origin-resource-policy":"cross-origin","x-goog-stored-content-length":"1131","cache-control":"public, max-age=31536000, immutable","x-goog-storage-class":"STANDARD"},"url":"https://jsr.io/@std/semver/1.0.3/try_parse.ts","time":1731060601}